import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Image from '../AppImage';

const NavigationBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOnAboutSection, setIsOnAboutSection] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navigationItems = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'Products', path: '/products' },
    { label: 'Contact', path: '/contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 20);

      // Check if we're on the homepage to apply dynamic styling
      if (location.pathname === '/') {
        const aboutSection = document.getElementById('about');
        if (aboutSection) {
          const aboutRect = aboutSection.getBoundingClientRect();
          const navBarHeight = 80;
          // Check if about section is visible in the viewport considering nav bar height
          setIsOnAboutSection(aboutRect.top <= navBarHeight && aboutRect.bottom > navBarHeight);
        }
      } else {
        setIsOnAboutSection(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const navigateToPage = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActivePage = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  };

  // Determine navbar styling based on page and scroll position
  const isHomePage = location.pathname === '/';
  const navBarClasses = isHomePage 
    ? (isOnAboutSection 
        ? 'fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-sand/30 shadow-lg transition-all duration-400' // Glass effect when on about section
        : 'fixed top-0 left-0 right-0 z-50 transition-all duration-400') // Transparent on hero section
    : 'fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-sand/30 shadow-lg transition-all duration-400'; // Glass effect on other pages

  // Determine text colors based on section
  const textColorClasses = isHomePage
    ? (isOnAboutSection 
        ? 'text-charcoal hover:text-primary' // Brand charcoal text on about section
        : 'text-white hover:text-secondary') // White text on hero section with gold hover
    : 'text-charcoal hover:text-primary'; // Default colors on other pages

  const activeTextColorClasses = isHomePage
    ? (isOnAboutSection 
        ? 'text-primary font-semibold bg-primary/10' // Green active on about section
        : 'text-secondary font-semibold bg-secondary/20') // Gold active on hero section
    : 'text-primary font-semibold bg-primary/10'; // Default active on other pages

  const logoTextColorClasses = isHomePage
    ? (isOnAboutSection 
        ? 'text-charcoal group-hover:text-primary' // Brand charcoal logo text on about section
        : 'text-white group-hover:text-secondary') // White logo text on hero section
    : 'text-charcoal group-hover:text-primary'; // Default logo colors on other pages

  const logoSubtextColorClasses = isHomePage
    ? (isOnAboutSection 
        ? 'text-charcoal-600' // Charcoal subtext on about section
        : 'text-white/80') // White subtext on hero section
    : 'text-charcoal-600'; // Default subtext on other pages

  const mobileButtonColorClasses = isHomePage
    ? (isOnAboutSection 
        ? 'text-charcoal hover:text-primary hover:bg-primary/10' // Charcoal mobile button on about section
        : 'text-white hover:text-secondary hover:bg-secondary/20') // White mobile button on hero section
    : 'text-charcoal hover:text-primary hover:bg-primary/10'; // Default mobile button on other pages

  return (
    <>
      <nav className={navBarClasses}>
        <div className="max-w-7xl mx-auto px-8 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <button
                onClick={() => navigateToPage('/')}
                className="flex items-center space-x-4 group"
              >
                <div className="h-20 w-auto flex items-center justify-center group-hover:scale-105 transition-all duration-300">
                  <Image
                    src="/assets/images/logo.png"
                    alt="Exquisite Coral Africa Limited Logo"
                    className="h-full w-auto object-contain max-w-none"
                  />
                </div>
                <div className="hidden sm:block">
                  <h1 className={`text-xl font-bold transition-colors duration-300 font-montserrat ${logoTextColorClasses}`}>
                    Exquisite Coral Africa
                  </h1>
                  <p className={`text-xs font-medium font-montserrat transition-colors duration-300 ${logoSubtextColorClasses}`}>
                    Premium Export Solutions
                  </p>
                </div>
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => navigateToPage(item.path)}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg font-montserrat ${
                    isActivePage(item.path)
                      ? activeTextColorClasses
                      : textColorClasses
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:flex items-center space-x-4">
              <button
                onClick={() => navigateToPage('/contact')}
                className="bg-primary hover:bg-primary-700 text-white px-8 py-3 text-sm font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl font-montserrat"
              >
                Book Consultation
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={toggleMobileMenu}
                className={`p-3 rounded-lg transition-all duration-300 ${mobileButtonColorClasses}`}
                aria-label="Toggle mobile menu"
              >
                <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm" onClick={toggleMobileMenu}></div>
          <div className="absolute top-0 right-0 w-80 h-full bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between mb-10">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-auto flex items-center justify-center">
                    <Image
                      src="/assets/images/logo.png"
                      alt="Exquisite Coral Africa Limited Logo"
                      className="h-full w-auto object-contain max-w-none"
                    />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-charcoal font-montserrat">Exquisite Coral Africa</h2>
                    <p className="text-xs text-charcoal-600 font-montserrat">Premium Export Solutions</p>
                  </div>
                </div>
                <button
                  onClick={toggleMobileMenu}
                  className="p-3 text-charcoal hover:text-primary hover:bg-primary/10 rounded-lg transition-all duration-300"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              {/* Mobile Navigation Items */}
              <div className="space-y-3 mb-10">
                {navigationItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => navigateToPage(item.path)}
                    className={`w-full text-left px-6 py-4 text-base font-medium transition-all duration-300 rounded-lg font-montserrat ${
                      isActivePage(item.path)
                        ? 'text-primary font-semibold bg-primary/10' 
                        : 'text-charcoal hover:text-primary hover:bg-primary/5'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Mobile CTA Button */}
              <button
                onClick={() => navigateToPage('/contact')}
                className="w-full bg-primary hover:bg-primary-700 text-white px-6 py-4 text-base font-semibold rounded-lg transition-all duration-300 shadow-lg font-montserrat"
              >
                Book Consultation
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavigationBar;